import React, { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Checkbox, InputField, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { ListSkeleton } from '../../components/Skeletons';
import { DataScope } from '../../graphql/__generated__/schema';
import {
    AccountActivitiesDocument,
    MembershipPerimeterSelectionDocument,
    UpdateMembershipPerimeterDocument,
} from '../../graphql/__generated__/types';
import ActivityPicker from './onboarding/ActivityPicker';
import { perimeterRefusalOf } from './perimeterRefusal';

const SEARCH_DEBOUNCE_MS = 300;

interface Props {
    membershipId: string;
    accountName: string;
    onClose: () => void;
    onSaved: () => void;
}

/**
 * The mutation takes the full desired state, not a diff: an unchecked box means remove it.
 * Ticked ids outside the searched subset stay in state and are sent, or a search would wipe
 * the perimeter it doesn't show. The search field lives above the picker, never inside it.
 */
const EditPerimeterDialog = ({ membershipId, accountName, onClose, onSaved }: Props) => {
    const {
        data: selectionData,
        loading: selectionLoading,
        error: selectionError,
    } = useQuery(MembershipPerimeterSelectionDocument, { variables: { membershipId }, fetchPolicy: 'network-only' });

    const selection = selectionData?.membershipPerimeterSelection ?? null;

    // initialised guards against re-seeding on every render, which would wipe the operator's ticks.
    const [scope, setScope] = useState<DataScope>(DataScope.All);
    const [needIds, setNeedIds] = useState<string[]>([]);
    const [serviceIds, setServiceIds] = useState<string[]>([]);
    const [initialised, setInitialised] = useState(false);

    useEffect(() => {
        if (!selection || initialised) {
            return;
        }
        setScope(selection.scope ?? DataScope.All);
        setNeedIds(selection.needIds ?? []);
        setServiceIds(selection.serviceIds ?? []);
        setInitialised(true);
    }, [selection, initialised]);

    const restricted = scope === DataScope.Restricted;

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    // The account travels as an argument, never as x-organization-id: this screen acts on an
    // account that is not the actor's, and the header would fail as an authorization refusal.
    const {
        data: activityData,
        previousData: previousActivityData,
        loading: activitiesLoading,
        error: activitiesError,
        refetch: refetchActivities,
    } = useQuery(AccountActivitiesDocument, {
        variables: {
            organizationId: selection?.organizationId ?? '',
            email: selection?.email ?? '',
            search: debouncedSearch,
        },
        skip: !restricted || !selection,
    });

    const activities = activityData?.accountActivities ?? previousActivityData?.accountActivities ?? null;

    const selectedCount = needIds.length + serviceIds.length;

    const toggleNeed = (needId: string, checked: boolean) =>
        setNeedIds((prev) => (checked ? [...prev, needId] : prev.filter((id) => id !== needId)));

    const toggleService = (serviceId: string, checked: boolean) =>
        setServiceIds((prev) => (checked ? [...prev, serviceId] : prev.filter((id) => id !== serviceId)));

    // Ticking a market ticks its displayed services; the market itself is never saved.
    const toggleMarket = (ids: string[], checked: boolean) =>
        setServiceIds((prev) => (checked ? [...new Set([...prev, ...ids])] : prev.filter((id) => !ids.includes(id))));

    const [save, { loading: saving, error: saveError }] = useMutation(UpdateMembershipPerimeterDocument);

    const emptyRestriction = restricted && selectedCount === 0;

    const submit = () => {
        save({
            variables: {
                membershipId,
                scope,
                needIds: restricted ? needIds : [],
                serviceIds: restricted ? serviceIds : [],
            },
        })
            .then((result) => {
                if (result.data) {
                    onSaved();
                    onClose();
                }
            })
            .catch(() => {});
    };

    return (
        <Modal open onClose={onClose} title={c('Perimeter').t`Change the perimeter`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-600">
                        {c('Perimeter').t`On the account`} <b className="text-text-900">{accountName}</b>
                    </p>

                    {selectionLoading && !selection && <ListSkeleton rows={4} withControl />}

                    {selectionError && (
                        <Alert type="warning">
                            {c('Perimeter')
                                .t`The current perimeter could not be read, so nothing can be changed safely. Close this and try again.`}
                        </Alert>
                    )}

                    {selection && (
                        <>
                            <label className="flex items-start gap-2 text-sm text-text-900">
                                <Checkbox
                                    id="perimeter-all"
                                    checked={scope === DataScope.All}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        setScope(event.target.checked ? DataScope.All : DataScope.Restricted)
                                    }
                                >
                                    <span className="flex flex-col">
                                        <span>{c('Perimeter').t`All the account's activities`}</span>
                                        <span className="text-xs text-text-600">
                                            {c('Perimeter')
                                                .t`The person sees everything on this account. Uncheck to limit them to chosen activities.`}
                                        </span>
                                    </span>
                                </Checkbox>
                            </label>

                            {restricted && (
                                <div className="border-t border-border-norm pt-4">
                                    <div className="mb-3 flex flex-wrap items-center gap-3">
                                        <div className="grow">
                                            <InputField
                                                id="perimeter-search"
                                                label={c('Perimeter').t`Search an activity`}
                                                type="search"
                                                value={search}
                                                onValue={setSearch}
                                            />
                                        </div>
                                        <span className="shrink-0 rounded-full border border-border-norm px-3 py-1 text-xs text-text-600">
                                            {selectedCount === 0
                                                ? c('Perimeter').t`No activity selected`
                                                : c('Perimeter').t`${selectedCount} selected`}
                                        </span>
                                    </div>

                                    {activitiesLoading && !activities && <ListSkeleton rows={6} withControl />}

                                    {activitiesError && (
                                        <div className="mb-3 flex flex-wrap items-center gap-3">
                                            <p className="text-xs text-danger-700" role="alert">
                                                {c('Perimeter')
                                                    .t`The activities of this account could not be read, so nothing is listed.`}
                                            </p>
                                            <Button category="weak" size="small" onClick={() => refetchActivities()}>
                                                {c('Perimeter').t`Try again`}
                                            </Button>
                                        </div>
                                    )}

                                    {activities && (
                                        <ActivityPicker
                                            activities={activities}
                                            needIds={needIds}
                                            serviceIds={serviceIds}
                                            onToggleNeed={toggleNeed}
                                            onToggleService={toggleService}
                                            onToggleMarket={toggleMarket}
                                        />
                                    )}
                                </div>
                            )}

                            {saveError && <RefusalNotice refusal={perimeterRefusalOf(saveError)} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={saving}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button
                    category="default"
                    loading={saving}
                    disabled={!selection || emptyRestriction}
                    title={
                        emptyRestriction
                            ? c('Perimeter').t`Tick at least one activity, or grant the whole account.`
                            : undefined
                    }
                    onClick={submit}
                >
                    {c('Action').t`Save`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default EditPerimeterDialog;
